import React, { useEffect, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { showToast } from '../../utils/toast';
import './CircleMemberList.css';

const ROLE_LABELS = { owner: 'オーナー', member: 'メンバー' };

function formatDate(value) {
  if (!value) return '';
  const d = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: 'short', day: 'numeric' });
}

function CircleMemberList({ circleId, isOwner }) {
  const { currentUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');

  const loadMembers = () => {
    if (!circleId) return;
    setLoading(true);
    getDocs(query(
      collection(db, 'recruitments', circleId, 'members'),
      where('status', '==', 'active'),
      orderBy('joinedAt', 'asc'),
    ))
      .then((snap) => setMembers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch((err) => console.error('CircleMemberList fetch failed:', err))
      .finally(() => setLoading(false));
  };

  useEffect(loadMembers, [circleId]);

  const handleRemove = async (member) => {
    if (!window.confirm(`「${member.nickname || member.id}」さんをサークルから削除しますか？`)) return;
    setSaving(member.id);
    try {
      await updateDoc(doc(db, 'recruitments', circleId, 'members', member.id), {
        status: 'removed',
        removedAt: serverTimestamp(),
        removedBy: currentUser.uid,
      });
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
      showToast({ message: 'メンバーを削除しました。', type: 'success' });
    } catch (err) {
      console.error('Member remove failed:', err);
      showToast({ message: '削除に失敗しました。', type: 'error' });
    } finally {
      setSaving('');
    }
  };

  const handleLeave = async () => {
    if (!window.confirm('サークルから退会しますか？')) return;
    setSaving(currentUser.uid);
    try {
      await updateDoc(doc(db, 'recruitments', circleId, 'members', currentUser.uid), {
        status: 'removed',
        removedAt: serverTimestamp(),
        removedBy: currentUser.uid,
      });
      setMembers((prev) => prev.filter((m) => m.id !== currentUser.uid));
      showToast({ message: 'サークルから退会しました。', type: 'success' });
    } catch (err) {
      console.error('Member leave failed:', err);
      showToast({ message: '退会処理に失敗しました。', type: 'error' });
    } finally {
      setSaving('');
    }
  };

  const handleRoleChange = async (member, newRole) => {
    setSaving(member.id);
    try {
      await updateDoc(doc(db, 'recruitments', circleId, 'members', member.id), {
        role: newRole,
        updatedAt: serverTimestamp(),
      });
      setMembers((prev) => prev.map((m) => m.id === member.id ? { ...m, role: newRole } : m));
      showToast({ message: 'ロールを変更しました。', type: 'success' });
    } catch (err) {
      console.error('Role change failed:', err);
      showToast({ message: 'ロール変更に失敗しました。', type: 'error' });
    } finally {
      setSaving('');
    }
  };

  if (loading) return <p className="cml-loading">読み込み中...</p>;

  const myMembership = members.find((m) => m.id === currentUser?.uid);
  const isMember = !!myMembership;
  const isMe = (id) => id === currentUser?.uid;

  return (
    <div className="cml-container">
      <div className="cml-summary">
        <span>{members.length}人のメンバー</span>
        {isMember && myMembership.role !== 'owner' && (
          <button
            type="button"
            className="cml-leave-btn"
            onClick={handleLeave}
            disabled={!!saving}
          >
            退会する
          </button>
        )}
      </div>

      {members.length === 0 ? (
        <p className="cml-empty">まだメンバーがいません。応募が承認されると表示されます。</p>
      ) : (
        <ul className="cml-list">
          {members.map((member) => (
            <li key={member.id} className="cml-item">
              <div className="cml-item__avatar">
                {member.photoURL
                  ? <img src={member.photoURL} alt={member.nickname} />
                  : <span>{(member.nickname || '?')[0]}</span>
                }
              </div>
              <div className="cml-item__info">
                <strong>{member.nickname || member.id}{isMe(member.id) && ' (あなた)'}</strong>
                <span>{formatDate(member.joinedAt)}</span>
              </div>
              <div className="cml-item__actions">
                <span className={`cml-role cml-role--${member.role}`}>
                  {ROLE_LABELS[member.role] || member.role}
                </span>
                {isOwner && !isMe(member.id) && member.role !== 'owner' && (
                  <>
                    <select
                      className="cml-role-select"
                      value={member.role}
                      onChange={(e) => handleRoleChange(member, e.target.value)}
                      disabled={saving === member.id}
                      aria-label={`${member.nickname}のロール変更`}
                    >
                      <option value="member">メンバー</option>
                    </select>
                    <button
                      type="button"
                      className="cml-remove-btn"
                      onClick={() => handleRemove(member)}
                      disabled={saving === member.id}
                    >
                      {saving === member.id ? '...' : '削除'}
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export async function addCircleMember(circleId, userId, profile) {
  const batch = writeBatch(db);
  batch.set(doc(db, 'recruitments', circleId, 'members', userId), {
    role: 'member',
    status: 'active',
    joinedAt: serverTimestamp(),
    nickname: profile?.nickname || '',
    photoURL: profile?.photoURL || '',
  });
  await batch.commit();
}

export default CircleMemberList;
